import React, { useState } from 'react'
import styles from '../CustomDomainModal.module.css'
import { getFirstPart, CopyIcon } from '../constants/utils'
import CustomTooltip from 'components/CustomTooltip'
import { useCustomDomain } from '../context/CustomDomainProvider'
import { Spinner, Alert, Accordion } from 'react-bootstrap'
import { ErrorBox } from 'components/ErrorBox/ErrorBox'
import { ERROR_MSGS, MSGS } from '../../../constants'

const DNSTable = ({ data, domain, DNSRecordsDiagnosticsProps, loading }) => {
  const { DNSRecordsDiagnostics } = useCustomDomain()
  const diagnosticsData = DNSRecordsDiagnosticsProps || DNSRecordsDiagnostics

  const isSubdomain =
    (!data?.isRootDomain && data?.subdomain) || (!data?.domainRecord?.isRootDomain && data?.domainRecord?.subdomain)
  const subdomain = getFirstPart(data?.subdomain || data?.domainRecord?.subdomain || '')

  if (diagnosticsData?.error) {
    const errorCode = diagnosticsData.error.code
    const errorMessage = ERROR_MSGS[errorCode]
    if (errorMessage) {
      return <ErrorBox message={errorMessage} hideButton />
    } else {
      return <ErrorBox message="Oops, something went wrong" hideButton />
    }
  }

  if (diagnosticsData?.message && diagnosticsData?.success) {
    const messageCode = diagnosticsData.message
    const message = MSGS[messageCode]
    if (message) {
      return <ErrorBox message={message} hideButton hideContactSupport />
    }
  }

  if (
    diagnosticsData &&
    (!diagnosticsData?.success || DNSRecordsDiagnosticsProps) &&
    diagnosticsData?.records?.length
  ) {
    const mismatchedRecords = diagnosticsData.records.filter((r) => !r.matches)
    const matchedRecords = diagnosticsData.records.filter((r) => r.matches)

    return (
      <>
        {loading ? (
          <div className={styles.spinnerContainer}>
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            {/* Summary Alert */}
            {mismatchedRecords.length > 0 && (
              <Alert variant="warning" className={styles.diagnosticsSummary}>
                <p className={styles.summaryText}>
                  {mismatchedRecords.length} DNS {mismatchedRecords.length === 1 ? 'record' : 'records'}{' '}
                  {mismatchedRecords.length === 1 ? 'does' : 'do'} not match the expected configuration. Please update{' '}
                  {mismatchedRecords.length === 1 ? 'this record' : 'these records'} with your DNS provider.
                </p>
              </Alert>
            )}

            <Accordion defaultActiveKey="0" alwaysOpen={false} className="no-border-accordion">
              {mismatchedRecords.length > 0 && (
                <Accordion.Item eventKey="0">
                  <Accordion.Header className={styles.sectionHeader}>
                    <span className={styles.errorIcon}>⚠️</span>
                    <span style={{ marginLeft: '8px' }}>fix these DNS records ({mismatchedRecords.length})</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {mismatchedRecords.map((record, index) => (
                      <RecordCard
                        key={`mismatch-${index}`}
                        record={record}
                        isSubdomain={isSubdomain}
                        subdomain={subdomain}
                        isMismatch={true}
                      />
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {matchedRecords.length > 0 && (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <span className={styles.successIcon}>✓</span>
                    <span style={{ marginLeft: '8px' }}>Correctly Configured Records ({matchedRecords.length})</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {matchedRecords.map((record, index) => (
                      <RecordCard
                        key={`match-${index}`}
                        record={record}
                        isSubdomain={isSubdomain}
                        subdomain={subdomain}
                        isMismatch={false}
                      />
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>

            {/* Help section */}
            {mismatchedRecords.length > 0 && (
              <div className={styles.diagnosticsHelp}>
                <h6>How to fix DNS records:</h6>
                <ol>
                  <li>Log in to your domain registrar or DNS provider</li>
                  <li>Navigate to DNS settings / DNS management</li>
                  <li>Update the records marked with ⚠️ to match the "Expected Value"</li>
                  <li>Save your changes</li>
                  <li>Wait 5-15 minutes for DNS propagation</li>
                  <li>Click "Re-validate" to check again</li>
                </ol>
              </div>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <>
      {loading ? (
        <div className={styles.spinnerContainer}>
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        data &&
        data?.records &&
        data?.records.map((record, index) => {
          const isWildcard = record?.host === '*'
          return (
            <div className={styles.table} key={index}>
              <div className={styles.record}>
                <span className={styles.dnsLabel}>Type:</span>
                <span className={styles.dnsValue}>
                  {record?.type}
                  <CopyIcon text={record?.type} size={12} />
                </span>
              </div>
              <div className={styles.record}>
                <span className={styles.dnsLabel}>Name/Host:</span>
                <span className={styles.dnsValue}>
                  {isWildcard && isSubdomain ? subdomain : getFirstPart(record?.host)}{' '}
                  <CopyIcon text={getFirstPart(record?.host)} size={12} />
                </span>
              </div>
              <div className={styles.record}>
                <span className={styles.dnsLabel}>Value:</span>
                <span className={styles.dnsValue}>
                  {record?.value}
                  <CopyIcon text={record?.value} size={12} />
                </span>
              </div>
            </div>
          )
        })
      )}
    </>
  )
}

const RecordCard = ({ record, isSubdomain, subdomain, isMismatch }) => {
  const isWildcard = record.name === '*'
  const hasNoRecords = !record.found || record.found.length === 0

  return (
    <div className={`${styles.recordCard} ${isMismatch ? styles.recordCardError : styles.recordCardSuccess}`}>
      <div className={styles.recordGrid}>
        <div className={styles.recordRow}>
          <span className={styles.recordLabel}>Type:</span>
          <span className={styles.recordValue}>
            <code>{record.type}</code>
            <CopyIcon text={record.type} size={12} />
          </span>
        </div>

        <div className={styles.recordRow}>
          <span className={styles.recordLabel}>Name/Host:</span>
          <span className={styles.recordValue}>
            <code>{record.name}</code>
            <CopyIcon text={record.name} size={12} />
          </span>
        </div>

        {isWildcard && isSubdomain && (
          <>
            <div className={styles.orDivider}>
              <span>Or</span>
            </div>
            <div className={styles.recordRow}>
              <span className={styles.recordLabel}>Name/Host:</span>
              <span className={styles.recordValue}>
                <code>{subdomain}</code>
                <CopyIcon text={subdomain} size={12} />
              </span>
            </div>
          </>
        )}

        <div className={styles.recordComparison}>
          <div className={styles.comparisonRow}>
            <span className={styles.recordLabel}>Expected Value:</span>
            <span className={styles.recordValue}>
              <code className={styles.expectedValue}>{record.expected}</code>
              <CopyIcon text={record.expected} size={12} />
            </span>
          </div>

          <div className={styles.comparisonRow}>
            <span className={styles.recordLabel}>{isMismatch ? 'Current Value:' : 'Found:'}</span>
            <span className={styles.recordValue}>
              {hasNoRecords ? (
                <span className={styles.noRecordFound}>❌ No DNS record found</span>
              ) : (
                <>
                  <code className={isMismatch ? styles.mismatchValue : styles.matchValue}>
                    {record.found.join(', ')}
                  </code>
                  {!isMismatch && <span className={styles.checkIcon}>✓</span>}
                </>
              )}
            </span>
          </div>
        </div>

        {isMismatch && (
          <div className={styles.recordAction}>
            <span className={styles.actionText}>
              {hasNoRecords
                ? '→ Add this DNS record with your provider'
                : '→ Update this DNS record to match the expected value'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default DNSTable
